import { FunctionComponent, memo } from "react";
import { Page, PageContent, PageHeader } from "@edgetier/client-components";
import { useSpellingExemptions } from "hooks-for/writing-issues/use-spelling-exemptions";
import { SpinnerUntil } from "@edgetier/components";
import SpellingConfigurationTable from "./spelling-configuration-table";
import SpellingConfigurationFilters from "./spelling-configuration-filters";
import { useQueryParameters } from "@edgetier/client-components";
import { IProps } from "./spelling-configuration.types";
import "./spelling-configuration.scss";

/**
 * A page for displaying and updating a list with industry / client specific keywords
 * which shouldn’t be marked as spelling mistakes
 */
const SpellingConfiguration: FunctionComponent<IProps> = () => {
    const { search, languageIsoCode } = useQueryParameters({
        search: String,
        languageIsoCode: String,
    });

    const orderBy = "exemption.asc";

    const { data, isLoading, isFetching, fetchNextPage, fetchPreviousPage, hasNextPage, hasPreviousPage, resetPage } =
        useSpellingExemptions({ orderBy, search, languageIsoCode });

    return (
        <Page className="spelling-configuration">
            <PageHeader title="Spelling Configuration" />
            <PageContent>
                <div className="spelling-configuration__container">
                    <SpellingConfigurationFilters
                        initialValues={{ search: search ?? "", languageIsoCode }}
                        resetPage={resetPage}
                    />
                    <SpinnerUntil data={data} isReady={!isLoading}>
                        {typeof data === "undefined" ? (
                            <>Error</>
                        ) : (
                            <SpellingConfigurationTable
                                spellingExemptions={isFetching ? null : data.items}
                                goToNextPage={fetchNextPage}
                                goToPreviousPage={fetchPreviousPage}
                                hasNext={hasNextPage}
                                hasPrevious={hasPreviousPage}
                            />
                        )}
                    </SpinnerUntil>
                </div>
            </PageContent>
        </Page>
    );
};

export default memo(SpellingConfiguration);
